import { useMutation, useQuery } from '@apollo/client';
import {
  GQL_CREATE_BATTLE,
  GQL_GET_AVAILABLE_CHALLENGERS,
  StoredChallengeableUser,
} from '~/gql/battles/battles';
import { authClient } from '~/helpers/apollo';
import styled from 'styled-components';
import { useLingui } from '@lingui/react';
import Button from '~/components/ui/button';
import iconStar from '@assets/images/icon/icon-star-gold.png';
import { classic } from '~/themes';
import iconBattle from '@assets/images/background/mode-battle.svg';
import {
  SInput,
  SSpan,
  SSpanTableAdvice,
  SSpanTableName,
} from '~/components/styles/styles';
import { WonBattleItem } from '~/components/ui/won-battle-item';
import { renderFullPath } from '~/utils/common/utils';
import { useEffect, useState } from 'react';
import { BattleStatus } from '@zeus/index';
import { useNavigate } from 'react-router-dom';
import ModalListAuto from '~/components/ui/modal-list-auto';
import { WrapperListAuto } from '~/components/ui/wrapper-list-auto';
import { SearchOutlined } from '@ant-design/icons';
import { useDebouncedState } from '~/helpers/hooks/debounce-state';
import { Loading } from '~/components/ui/loading';

const PickChallenger = () => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [friends, setFriends] = useState<StoredChallengeableUser[]>([]);
  const [users, setUsers] = useState<StoredChallengeableUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [searchText, setSearchText, debouncedSearchText] = useDebouncedState(
    '',
    500
  );

  const { refetch: fetchUsers } = useQuery(GQL_GET_AVAILABLE_CHALLENGERS, {
    client: authClient,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: { searchText: debouncedSearchText },
    onCompleted: (data) => {
      if (data) {
        setFriends(data.getAvailableChallengers.challengeableFriends);
        setUsers(data.getAvailableChallengers.challengeableUsers);
      }
    },
  });

  const [createBattle] = useMutation(GQL_CREATE_BATTLE, {
    client: authClient,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    void getUsers;
  }, []);

  async function getUsers() {
    setLoading(true);
    await fetchUsers();
    setLoading(false);
  }

  function getRandomUser(): StoredChallengeableUser | null {
    const allUsers = [...friends, ...users];
    if (allUsers.length === 0) {
      return null;
    }
    const randomIndex = Math.floor(Math.random() * allUsers.length);
    return allUsers[randomIndex];
  }

  async function handleChooseRandomUser() {
    const randomUser = getRandomUser();

    if (randomUser) {
      createNewBattle(randomUser.id);
    }
  }

  async function createNewBattle(userId: string) {
    if (!userId) return;
    const { data } = await createBattle({
      variables: {
        challengedId: userId,
      },
    });
    if (data) {
      navigate(
        `/mode-battle/defis/${data.createBattle.battlePlayerTrackingId}`
      );
    }
  }

  return (
    <ModalListAuto
      iconSrc={iconBattle}
      open={true}
      onClose={() => navigate('/mode-battle')}
    >
      <WrapperListAuto
        onClose={() => navigate('/mode-battle')}
        title={`${i18n._('common.choose.anyone')}`}
        footerChildren={
          <>
            <Button
              width="100%"
              withoutTopBorderRadius
              margin="0"
              onClick={handleChooseRandomUser}
              color={'primary'}
            >
              {i18n._('battle.random.player')}
            </Button>
          </>
        }
      >
        {loading ? (
          <Loading />
        ) : (
          <Container>
            <SDivSearch>
              <SInput
                placeholder={i18n._('common.search')}
                onChange={(e) => setSearchText(e.target.value)}
                size="large"
                suffix={
                  <SearchOutlined
                    style={{
                      color: `${classic.token?.colorPrimary}`,
                    }}
                  />
                }
                value={searchText}
              />
            </SDivSearch>
            <STableWrapper>
              <SSpan $color={classic.token?.colorText}>
                {i18n._('common.friends.my')}
                {` (${friends.length})`}
              </SSpan>
              <STable>
                <tbody>
                  {friends.map((user, indUser) => (
                    <tr key={indUser} onClick={() => createNewBattle(user.id)}>
                      <td>
                        <WonBattleItem wonBattles={user.wonBattles} />
                      </td>
                      <td>
                        {user.avatar?.fileName && (
                          <SDivAvatar>
                            <img
                              src={renderFullPath(user.avatar?.fileName)}
                              alt=""
                            />
                          </SDivAvatar>
                        )}
                      </td>
                      <td>
                        <SDivName>
                          <SSpanTableName>{user.username}</SSpanTableName>
                          <SSpanTableAdvice className="hidden">
                            {user.battleStatus === BattleStatus.IN_PROGRESS ||
                            user.battleStatus === BattleStatus.PENDING
                              ? i18n._('battle.battle.in.progress')
                              : i18n._('battle.proposition.challenge')}
                          </SSpanTableAdvice>
                        </SDivName>
                      </td>
                      <td>
                        <SDivFriend>
                          <img src={iconStar} alt="" />
                        </SDivFriend>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </STable>
              <SDivSeparator></SDivSeparator>

              <SSpan $color={classic.token?.colorText}>
                {i18n._('common.users.all')}
                {` (${users.length})`}
              </SSpan>

              <STable>
                <tbody>
                  {users.map((user, indUser) => (
                    <tr key={indUser} onClick={() => createNewBattle(user.id)}>
                      <td>
                        <WonBattleItem wonBattles={user.wonBattles} />
                      </td>
                      <td>
                        {user.avatar?.fileName && (
                          <SDivAvatar>
                            <img
                              src={renderFullPath(user.avatar?.fileName)}
                              alt=""
                            />
                          </SDivAvatar>
                        )}
                      </td>
                      <td>
                        <SDivName>
                          <SSpanTableName>{user.username}</SSpanTableName>
                          <SSpanTableAdvice className="hidden">
                            {user.battleStatus === BattleStatus.IN_PROGRESS ||
                            user.battleStatus === BattleStatus.PENDING
                              ? i18n._('battle.battle.in.progress')
                              : i18n._('battle.proposition.challenge')}
                          </SSpanTableAdvice>
                        </SDivName>
                      </td>
                      <td></td>
                    </tr>
                  ))}
                </tbody>
              </STable>
            </STableWrapper>
          </Container>
        )}
      </WrapperListAuto>
    </ModalListAuto>
  );
};

export default PickChallenger;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const SDivSearch = styled.div`
  padding: 20px;
  width: 100%;
`;

const SDivFriend = styled.button`
  background-color: transparent;
  height: 44px;
  width: 100%;
  position: relative;
  outline: none;
  border: none;
  &:hover {
    outline: none;
    border: none;
    transform: scale(1.1);
  }
  &:focus {
    outline: none;
    border: none;
    transform: scale(1.1);
  }

  img {
    position: absolute;
    object-fit: contain;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }
`;

const SDivName = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 44px;
  width: 100%;
`;

const SDivAvatar = styled.div`
  border-radius: 50%;
  background-color: #fff;
  height: 44px;
  width: 40px;
  position: relative;
  border: 3px solid #fff;

  img {
    position: absolute;
    object-fit: contain;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }
`;

const STableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  overflow-y: auto;
  padding: 20px 20px 130px 20px;

  @media screen and (max-height: 800px) {
    padding: 10px 10px 130px 40px;
  }

  @media screen and (max-height: 450px) {
    padding: 10px 10px 100px 40px;
  }

  @media screen and (max-width: 450px) {
    padding: 10px 10px 100px 10px;
  }
`;

const SDivSeparator = styled.div`
  height: 1px;
  margin: 10px 0;
  width: 100%;
  border: 1px solid #53d2d7;
`;

const STable = styled.table`
  text-align: left;
  position: relative;
  border-collapse: collapse;
  width: 100%;

  tbody {
    tr {
      &:nth-of-type(even) {
        user-select: none;
      }
      &:nth-of-type(odd) {
        background-color: transparent;
        user-select: none;
      }
      td {
        color: #113969;
        font-family: 'Riffic Free', sans-serif;
        background-color: transparent;
        font-weight: bold;
        font-size: 1.1rem;
        padding: 8px 12px;
        user-select: none;

        @media screen and (max-width: 450px) {
          font-size: 0.8rem;
          padding: 5px 8px;
        }
        @media screen and (max-width: 290px) {
          font-size: 0.7rem;
          padding: 5px 8px;
        }
        @media screen and (max-height: 600px) {
          font-size: 0.9rem;
          padding: 5px 8px;
        }
        @media screen and (max-height: 450px) {
          font-size: 0.8rem;
          padding: 5px 8px;
        }
        @media screen and (max-height: 290px) {
          font-size: 0.7rem;
          padding: 5px 8px;
        }

        &:first-of-type {
          text-align: center;
          width: 10%;
          user-select: none;
        }
        &:nth-of-type(2) {
          width: 10%;
          user-select: none;
        }
        &:nth-of-type(3) {
          width: 45%;
          user-select: none;
        }
        &:last-of-type {
          padding-right: 10px;
          text-align: right;
          width: 15%;
          user-select: none;
        }
      }

      & > td > div > span {
        transition: all 0.5s linear;
        &.hidden {
          visibility: hidden;
          height: 0;
          opacity: 0;
        }
      }

      &:hover {
        & > td > div > span {
          &.hidden {
            visibility: visible;
            height: auto;
            opacity: 1;
          }
        }
      }
    }
  }
`;
